import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  useMultiStepForm,
  Button,
  FormSelect,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { NOMINEE_RELATIONSHIP } from "creso-core/constants";
import {
  useCreateNomineeDetails,
  useNominees,
  useUpdateInvestorInvestmentAccount,
  useUpdateNomineeDetails,
} from "creso-core/hooks";
import { NomineeDetailsSchema } from "creso-core/zod";
import { toast } from "sonner";
import { useMemo } from "react";
import { APIError, handleError } from "../../utils";
import { useClientOnboarding } from "./context";

export function NomineeDetails() {
  const { client } = useClientOnboarding();
  const { data } = useNominees(client?.investment_account ?? "");
  const nomineeDetails = useMemo(() => data?.[0], [data]);
  const { previousStep, nextStep } = useMultiStepForm();

  // Only name and relationship are captured during onboarding. The remaining
  // nominee details are optional and can be added from the account centre.
  const form = useForm<z.infer<typeof NomineeDetailsSchema>>({
    resolver: zodResolver(NomineeDetailsSchema),
    values: {
      nominate: true,
      name: nomineeDetails?.name ?? "",
      relationship: nomineeDetails?.relationship ?? "",
      identity_number: nomineeDetails?.identity_number ?? "",
      identity_type: nomineeDetails?.identity_type ?? "",
      email: nomineeDetails?.email ?? "",
      mobile: nomineeDetails?.mobile ?? "",
      address_line_1: nomineeDetails?.address_line_1 ?? "",
      address_line_2: nomineeDetails?.address_line_2 ?? "",
      address_line_3: nomineeDetails?.address_line_3 ?? "",
      country: nomineeDetails?.country ?? "",
      city: nomineeDetails?.city ?? "",
      pin_code: nomineeDetails?.pin_code ?? "",
      applicable_percentage: nomineeDetails?.applicable_percentage ?? 100,
    },
  });

  const { mutateAsync: createNomineeDetails } = useCreateNomineeDetails();
  const { mutateAsync: updateNomineeDetails } = useUpdateNomineeDetails();

  const { mutateAsync: updateInvestmentAccount } =
    useUpdateInvestorInvestmentAccount(client?.investment_account ?? "");

  const onSubmit = async (data: z.infer<typeof NomineeDetailsSchema>) => {
    const payload = {
      name: data.name,
      relationship: data.relationship,
    };

    try {
      if (nomineeDetails?.id) {
        await updateNomineeDetails({
          ...nomineeDetails,
          ...payload,
          investment_account_guid: client?.investment_account ?? "",
        });
      } else {
        await updateInvestmentAccount({
          nominationOpt: "Y",
        });
        await createNomineeDetails({
          ...payload,
          investment_account_guid: client?.investment_account ?? "",
          applicable_percentage: 100,
        });
      }
      nextStep();
    } catch (e) {
      if (!(e instanceof APIError)) {
        toast.error("Failed to save nominee details");
        return;
      }
      const { code } = e.data;
      // Unfortunate code due to poor api design
      if (code === "VALIDATION_ERROR") {
        const { details } = e.data;
        Object.entries((details || {}) as Record<string, string>).forEach(
          ([key, value]) => {
            form.setError(key as keyof z.infer<typeof NomineeDetailsSchema>, {
              message: value,
            });
          }
        );
      } else {
        handleError(e, "Failed to save nominee details");
      }
    }
  };

  return (
    <div className="pb-24 relative size-full">
      <div className="flex flex-col gap-6 size-full overflow-auto p-6 pb-0">
        <span className="text-lg font-semibold">Nominee details</span>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 gap-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormInput {...field} value={field.value || ""} label="Name" />
              )}
            />
            <FormField
              control={form.control}
              name="relationship"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Relationship"
                  options={NOMINEE_RELATIONSHIP}
                />
              )}
            />
            <DialogFooter className="mt-auto p-6 w-full bg-background left-0 absolute bottom-0 sm:space-x-5">
              <Button variant="outline" type="button" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="w-full flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                Next
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </div>
    </div>
  );
}
